'use client'

import { m } from 'framer-motion'
import { useState } from 'react'

type TypographyCard = {
  label: string
  sample: string
  sampleClassName?: string
  fontName: string
  subtitle?: string
  meta?: string
  accent?: 'blue' | 'purple' | 'green' | 'gray'
}

type ColorSwatch = {
  hex: string
  label?: string
  glow?: string
}

export type CaseDesignSystemBlock = {
  eyebrow?: string
  title?: string
  description?: string
  typography?: TypographyCard[]
  colors?: ColorSwatch[]
}

function accentClass(accent: TypographyCard['accent']) {
  switch (accent) {
    case 'purple':
      return 'text-purple-300'
    case 'green':
      return 'text-emerald-300'
    case 'gray':
      return 'text-gray-300'
    case 'blue':
    default:
      return 'text-blue-300'
  }
}

export default function CaseDesignSystem({
  designSystem,
}: {
  designSystem?: CaseDesignSystemBlock | null
}) {
  const [activeSwatch, setActiveSwatch] = useState<number | null>(null)
  if (!designSystem) return null

  const typography =
    designSystem.typography && designSystem.typography.length > 0
      ? designSystem.typography.slice(0, 2)
      : ([
          {
            label: 'Основной стиль',
            sample: 'Aa',
            fontName: 'Inter Tight',
            subtitle: 'UI типографика',
            meta: '700 Bold • Tracking -2%',
            accent: 'blue',
          },
          {
            label: 'Акцентный стиль',
            sample: 'Aa',
            sampleClassName: 'font-serif italic text-white/80',
            fontName: 'Playfair Display',
            subtitle: 'Editorial serif',
            meta: '400 Regular • Italic',
            accent: 'purple',
          },
        ] as TypographyCard[])

  const colors =
    designSystem.colors && designSystem.colors.length > 0
      ? designSystem.colors.slice(0, 6)
      : ([
          { hex: '#5b21b6', label: 'Deep Violet', glow: 'shadow-[0_0_46px_rgba(91,33,182,0.35)]' },
          { hex: '#0ea5e9', label: 'Sky Blue', glow: 'shadow-[0_0_46px_rgba(14,165,233,0.35)]' },
          {
            hex: '#10b981',
            label: 'Signal Green',
            glow: 'shadow-[0_0_46px_rgba(16,185,129,0.35)]',
          },
          { hex: '#111827', label: 'Graphite', glow: 'shadow-[0_0_24px_rgba(148,163,184,0.18)]' },
        ] as ColorSwatch[])

  return (
    <section className="home-section relative overflow-hidden bg-transparent">
      <div className="home-container relative z-10">
        <div className="mb-10 max-w-4xl sm:mb-14">
          <m.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="home-meta mb-4 text-gray-400"
          >
            {designSystem.eyebrow ?? 'Design system'}
          </m.p>

          <m.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.06 }}
            className="home-title mb-5 text-white"
          >
            {designSystem.title ?? 'Типографика и цветовая система'}
          </m.h2>

          <m.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="home-subnote max-w-3xl text-gray-400"
          >
            {designSystem.description ??
              'Единые правила визуала обеспечивают узнаваемость и стабильный пользовательский опыт на всех экранах.'}
          </m.p>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-6 sm:mb-8 sm:gap-8 xl:grid-cols-2">
          {typography.map((item, i) => (
            <m.div
              key={`typo-${i}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md sm:p-8"
            >
              <span className={`home-meta ${accentClass(item.accent)} mb-4 inline-block`}>
                {item.label}
              </span>

              <div className="flex items-end justify-between gap-6">
                <div
                  className={[
                    'text-[clamp(3rem,10vw,6rem)] leading-none tracking-tight',
                    item.sampleClassName ?? 'font-display text-white',
                  ].join(' ')}
                >
                  {item.sample}
                </div>

                <div className="text-right">
                  <div className="home-card-title-sm text-white">{item.fontName}</div>
                  {item.subtitle ? (
                    <p className="home-subnote mt-1 text-gray-400">{item.subtitle}</p>
                  ) : null}
                  {item.meta ? <p className="home-meta mt-3 text-gray-500">{item.meta}</p> : null}
                </div>
              </div>
            </m.div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-4 sm:gap-5 md:grid-cols-4">
          {colors.map((color, i) => {
            const isActive = activeSwatch === i

            return (
              <m.div
                key={`${color.hex}-${i}`}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                role="button"
                tabIndex={0}
                aria-pressed={isActive}
                onClick={() => setActiveSwatch((prev) => (prev === i ? null : i))}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    setActiveSwatch((prev) => (prev === i ? null : i))
                  }
                }}
                className={[
                  'group flex min-h-[170px] cursor-pointer flex-col items-center justify-center rounded-2xl border bg-white/[0.03] p-4 text-center transition-all duration-300 select-none sm:min-h-[180px] sm:p-5 md:min-h-[160px] lg:min-h-[180px]',
                  isActive
                    ? 'border-cyan-300/45 bg-white/[0.08] shadow-[0_14px_42px_rgba(34,211,238,0.2)]'
                    : 'border-white/10 hover:bg-white/[0.06]',
                ].join(' ')}
              >
                <div className="flex h-16 items-center justify-center sm:h-20">
                  <div
                    className={[
                      'h-12 w-12 rounded-full border border-white/20 transition-all duration-500 sm:h-14 sm:w-14',
                      isActive ? 'scale-110' : 'group-hover:scale-110',
                      color.glow ?? '',
                    ].join(' ')}
                    style={{ backgroundColor: color.hex }}
                  />
                </div>
                <p
                  className={[
                    'home-meta mt-4 transition-all duration-300',
                    isActive
                      ? 'translate-y-0 text-gray-200 opacity-100'
                      : 'translate-y-1 text-gray-300 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100',
                  ].join(' ')}
                >
                  {color.label ?? 'Color'}
                </p>
                <p
                  className={[
                    'home-caption mt-1 transition-all duration-300',
                    isActive
                      ? 'translate-y-0 text-gray-400 opacity-100'
                      : 'translate-y-1 text-gray-500 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100',
                  ].join(' ')}
                >
                  {color.hex}
                </p>
              </m.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
